import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money, fmtTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Landmark } from "lucide-react";

export default function Reconcile() {
  const qc = useQueryClient();
  const [floatAmt, setFloatAmt] = useState("0");
  const [actual, setActual] = useState("");
  const { data: current } = useQuery({ queryKey: ["cash-current"], queryFn: async () => (await api.get("/cash/current")).data });
  const { data: history = [] } = useQuery({ queryKey: ["cash-history"], queryFn: async () => (await api.get("/cash/history")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";

  const open = async () => {
    try {
      await api.post("/cash/open", { opening_float: Number(floatAmt || 0) });
      toast.success("Shift opened");
      qc.invalidateQueries({ queryKey: ["cash-current"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const close = async () => {
    try {
      const { data } = await api.post("/cash/close", { actual_cash: Number(actual || 0) });
      toast.success(`Closed · variance ${money(data.variance, sym)}`);
      setActual("");
      qc.invalidateQueries({ queryKey: ["cash-current"] });
      qc.invalidateQueries({ queryKey: ["cash-history"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const expected = current ? Number(current.opening_float || 0) + Number(current.cash_sales || 0) : 0;

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="reconcile">
      <PageHeader title="Order Reconciliation" subtitle="Open/close cash drawer and match cash vs digital sales" />
      <div className="kul-card p-6 max-w-xl space-y-4">
        <div className="flex items-center gap-2 font-display font-700 text-lg">
          <Landmark className="w-5 h-5 text-[var(--kul-primary)]" /> {current ? "Shift open" : "No open shift"}
        </div>
        {!current ? (
          <div className="space-y-3">
            <div><Label>Opening float</Label><Input type="number" value={floatAmt} onChange={(e) => setFloatAmt(e.target.value)} /></div>
            <Button onClick={open} className="bg-[var(--kul-primary)] text-white">Open shift</Button>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span>Opened by</span><span>{current.opened_by}</span></div>
            <div className="flex justify-between"><span>Opening float</span><span>{money(current.opening_float, sym)}</span></div>
            <div className="flex justify-between"><span>Cash sales</span><span>{money(current.cash_sales, sym)}</span></div>
            <div className="flex justify-between"><span>Digital sales</span><span>{money(current.digital_sales, sym)}</span></div>
            <div className="flex justify-between font-semibold"><span>Expected in drawer</span><span>{money(expected, sym)}</span></div>
            <div><Label>Actual cash counted</Label><Input type="number" value={actual} onChange={(e) => setActual(e.target.value)} /></div>
            <Button onClick={close} className="bg-[var(--kul-primary)] text-white w-full">Close & reconcile</Button>
          </div>
        )}
      </div>
      <div className="max-w-xl mt-6 space-y-2">
        {history.filter((h) => h.status === "closed").map((h) => (
          <div key={h.id} className="kul-card p-4 text-sm flex justify-between">
            <div>
              <div className="font-medium">{h.closed_by} · {fmtTime(h.closed_at)}</div>
              <div className="text-neutral-500">Expected {money(h.expected_cash, sym)} · Counted {money(h.actual_cash, sym)}</div>
            </div>
            <div className={Number(h.variance) === 0 ? "text-green-600" : "text-[var(--kul-danger)]"}>
              {money(h.variance, sym)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
